import type { ReactNode } from 'react';
import type { DealView, JobView, ProjectView, ShortlistMemberView } from './client.ts';
import { BILLING_LABEL } from './record-data.ts';

/**
 * Card fields (RELAY-1038): what a board card shows under its title, chosen per view and kept
 * in the view's state (`card.fields`) beside its filter, so a saved view brings its cards back
 * exactly. Each list has a catalogue of fields and a default set; a field with no value on a
 * record is simply left off that card.
 */
export interface CardContext {
  nameOf: (userId: string | null) => string;
}
export interface CardField<T> {
  id: string;
  label: string;
  render: (item: T, ctx: CardContext) => ReactNode;
}
export interface CardFieldSet<T> {
  fields: ReadonlyArray<CardField<T>>;
  defaults: readonly string[];
}

const day = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString() : null);
const person = (ctx: CardContext, id: string | null) => (id ? ctx.nameOf(id) : null);

export const PROJECT_CARD: CardFieldSet<ProjectView> = {
  fields: [
    { id: 'company', label: 'Company', render: (p) => p.company.name },
    { id: 'owner', label: 'Owner', render: (p, ctx) => person(ctx, p.ownerId) },
    { id: 'description', label: 'Description', render: (p) => p.description },
    { id: 'created', label: 'Created', render: (p) => day(p.createdAt) },
    { id: 'updated', label: 'Updated', render: (p) => day(p.updatedAt) },
  ],
  defaults: ['company', 'owner'],
};

export const DEAL_CARD: CardFieldSet<DealView> = {
  fields: [
    {
      id: 'project',
      label: 'Project',
      render: (d) => `${d.project.name} · ${d.project.companyName}`,
    },
    {
      id: 'value',
      label: 'Value',
      render: (d) => `${Number(d.value).toLocaleString()} ${d.currency}`,
    },
    { id: 'billing', label: 'Billing', render: (d) => BILLING_LABEL[d.billingModel] },
    { id: 'expectedClose', label: 'Expected close', render: (d) => day(d.expectedCloseDate) },
    { id: 'paid', label: 'Paid', render: (d) => day(d.paidAt) },
    { id: 'invoice', label: 'Invoice', render: (d) => d.invoiceRef },
  ],
  defaults: ['project', 'value'],
};

export const JOB_CARD: CardFieldSet<JobView> = {
  fields: [
    {
      id: 'project',
      label: 'Project',
      render: (j) => `${j.project.name} · ${j.project.companyName}`,
    },
    { id: 'recruiter', label: 'Recruiter', render: (j, ctx) => person(ctx, j.recruiterId) },
    { id: 'coordinator', label: 'Coordinator', render: (j, ctx) => person(ctx, j.coordinatorId) },
    {
      id: 'accountManager',
      label: 'Account manager',
      render: (j, ctx) => person(ctx, j.accountManagerId),
    },
    { id: 'interview', label: 'Interview', render: (j) => day(j.interviewDateTime) },
    { id: 'openings', label: 'Filled', render: (j) => `${j.filled} / ${j.openings}` },
    { id: 'location', label: 'Location', render: (j) => j.location },
  ],
  defaults: ['project', 'recruiter', 'interview'],
};

export const SHORTLIST_CARD: CardFieldSet<ShortlistMemberView> = {
  fields: [
    { id: 'email', label: 'Email', render: (m) => m.candidate.email },
    { id: 'since', label: 'In stage since', render: (m) => day(m.stageEnteredAt) },
    { id: 'client', label: 'Client sees', render: (m) => (m.visibleToClient ? 'Yes' : 'No') },
    { id: 'reason', label: 'Rejection reason', render: (m) => m.rejectedReason },
    { id: 'added', label: 'Added', render: (m) => day(m.addedAt) },
  ],
  defaults: ['email', 'since'],
};
